import { Host, Picker } from '@expo/ui';
import { Link, Stack, router, type Href } from 'expo-router';
import {
  BellIcon,
  HomeIcon,
  LogOutIcon,
  MenuIcon,
  PlusIcon,
  RouteIcon,
  ScanLineIcon,
  SearchIcon,
  SettingsIcon,
  ShieldIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react-native';
import type { SFSymbol } from 'sf-symbols-typescript';
import * as React from 'react';
import {
  Modal,
  Platform,
  Pressable,
  TextInput,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useColorScheme } from 'nativewind';

import {
  MEMBER_HEADER_ACCOUNT_MIN_WIDTH,
  MEMBER_HEADER_CONTROL_SIZE,
} from '@/components/member-header-tokens';
import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { GlassControl, type GlassControlPhase } from '@sixam/ui/glass-control';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { BRAND } from '@sixam/config/brand';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';
import {
  openOperatorCheckIn,
  useActiveOperatorCheckIn,
} from '@/components/events/live-check-in-entry';
import type { LiveCheckInEvent } from '@/lib/event-checkins';

type MemberHeaderBarProps = {
  mode?: 'member' | 'admin';
  onCreatePress?: () => void;
  onSearchChange?: (query: string) => void;
  searchPlaceholder: string;
};

/**
 * Everything a signed-in member has as themselves. These are the same
 * destinations whether or not they help run a club, which is what keeps the
 * member path and the club path from blurring together.
 */
const baseDrawerItems = [
  { href: '/app/home' as Href, icon: HomeIcon, label: 'Home' },
  { href: '/app/me' as Href, icon: UserIcon, label: 'Me' },
  { href: '/app/settings' as Href, icon: SettingsIcon, label: 'Settings' },
];

const PERSONAL_SCOPE_VALUE = 'personal';
const CLUB_SCOPE_COLLAPSED_WIDTH = 216;

/**
 * The drawer, split so the two paths never blur together: everything under
 * "You" is this account acting as a member, and the club section only appears
 * for someone who actually administers a club. Shared by both platform drawers
 * so the two cannot drift apart again.
 */
function DrawerSections({
  adminLabel,
  canOpenAdmin,
  onClose,
  onOpenAdmin,
}: Readonly<{
  adminLabel: string | null;
  canOpenAdmin: boolean;
  onClose: () => void;
  onOpenAdmin: () => void;
}>) {
  const routeLibraryEnabled = useReleaseFeatureFlag('routeLibrary') === true;
  const eventRemindersEnabled = useReleaseFeatureFlag('eventReminders') === true;
  const drawerItems = [
    ...baseDrawerItems.slice(0, 1),
    ...(routeLibraryEnabled
      ? [{ href: '/app/routes' as Href, icon: RouteIcon, label: 'Routes' }]
      : []),
    ...baseDrawerItems.slice(1, 2),
    ...(eventRemindersEnabled
      ? [{ href: '/app/notifications' as Href, icon: BellIcon, label: 'Notifications' }]
      : []),
    ...baseDrawerItems.slice(2),
  ];

  return (
    <View className="gap-6">
      <View className="gap-1">
        <Text variant="label" className="text-muted-foreground">
          You
        </Text>
        {drawerItems.map((item) => (
          <Link asChild href={item.href} key={String(item.href)}>
            <Pressable
              className="flex-row items-center gap-3 border-b border-border py-4 active:opacity-70"
              onPress={onClose}
            >
              <Icon as={item.icon} className="size-5 text-foreground" />
              <Text className="text-lg font-bold">{item.label}</Text>
            </Pressable>
          </Link>
        ))}
      </View>

      {canOpenAdmin ? (
        <View className="gap-1">
          <Text variant="label" className="text-muted-foreground">
            Club
          </Text>
          <Pressable
            accessibilityLabel={
              adminLabel ? `Open admin view for ${adminLabel}` : 'Open admin view'
            }
            accessibilityRole="button"
            className="flex-row items-center gap-3 border-b border-border py-4 active:opacity-70"
            onPress={onOpenAdmin}
          >
            <Icon as={ShieldIcon} className="size-5 text-primary" />
            <View className="min-w-0 flex-1">
              <Text className="text-lg font-bold">Admin view</Text>
              <Text variant="muted" numberOfLines={1}>
                {adminLabel ? `Manage ${adminLabel}` : 'Manage your club'}
              </Text>
            </View>
          </Pressable>
        </View>
      ) : null}

      <DrawerSignOutButton onSignedOut={onClose} />
    </View>
  );
}

/**
 * Sits last in the drawer, after Settings. Signing out is destructive enough to
 * want visual separation from the navigation items above it, but it is not an
 * error, so it stays in the normal foreground rather than borrowing the
 * destructive colour used for failures.
 */
function DrawerSignOutButton({ onSignedOut }: Readonly<{ onSignedOut: () => void }>) {
  const { signOut } = useSession();
  const [isSigningOut, setIsSigningOut] = React.useState(false);

  async function handleSignOut() {
    if (isSigningOut) {
      return;
    }

    setIsSigningOut(true);
    try {
      await signOut();
      onSignedOut();
      router.replace('/(public)/sign-in');
    } finally {
      setIsSigningOut(false);
    }
  }

  return (
    <Pressable
      accessibilityLabel="Log out"
      accessibilityRole="button"
      accessibilityState={{ busy: isSigningOut, disabled: isSigningOut }}
      className="flex-row items-center gap-3 py-4 active:opacity-70"
      disabled={isSigningOut}
      onPress={handleSignOut}
    >
      <Icon as={LogOutIcon} className="size-5 text-foreground" />
      <Text className="text-lg font-bold">{isSigningOut ? 'Logging out...' : 'Log out'}</Text>
    </Pressable>
  );
}

export { MEMBER_HEADER_CONTROL_SIZE } from '@/components/member-header-tokens';

export function memberHeaderControlForeground(phase?: GlassControlPhase) {
  return phase === 'onDark' ? 'text-white' : 'text-foreground';
}

function toolbarIcon(icon: SFSymbol) {
  return process.env.EXPO_OS === 'ios' ? icon : undefined;
}

function toolbarFallbackLabel(label: string) {
  return process.env.EXPO_OS === 'ios' ? undefined : label;
}

export function MemberHeaderBar({
  searchPlaceholder,
  onCreatePress,
  onSearchChange,
}: Readonly<MemberHeaderBarProps>) {
  const activeCheckInEvent = useActiveOperatorCheckIn();

  if (Platform.OS === 'web') {
    return null;
  }

  if (Platform.OS === 'android') {
    return (
      <MemberHeaderBarAndroid
        activeCheckInEvent={activeCheckInEvent}
        onCreatePress={onCreatePress}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
      />
    );
  }

  return (
    <MemberHeaderBarNative
      activeCheckInEvent={activeCheckInEvent}
      onCreatePress={onCreatePress}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
    />
  );
}

function MemberHeaderBarAndroid({
  activeCheckInEvent,
  searchPlaceholder,
  onCreatePress,
  onSearchChange,
}: Readonly<MemberHeaderBarProps & { activeCheckInEvent: LiveCheckInEvent | null }>) {
  const { colorScheme } = useColorScheme();
  const insets = useSafeAreaInsets();
  const { isAdmin } = useSession();
  const { memberships, resolveDefaultSlug, tenants } = useTenant();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const isDark = colorScheme === 'dark';
  const foreground = isDark ? BRAND.color.white : BRAND.color.ink;
  const background = isDark ? BRAND.color.ink : BRAND.color.smoke;

  const canOpenAdmin =
    isAdmin ||
    memberships.some((membership) => {
      return membership.role === 'admin' || membership.role === 'owner';
    });

  const adminSlug = React.useMemo(() => {
    if (isAdmin) {
      return tenants[0]?.slug ?? resolveDefaultSlug();
    }
    const adminMembership = memberships.find(
      (membership) => membership.role === 'admin' || membership.role === 'owner'
    );
    return tenants.find((tenant) => tenant.id === adminMembership?.tenant_id)?.slug ?? null;
  }, [isAdmin, memberships, resolveDefaultSlug, tenants]);

  const adminTenantName = React.useMemo(
    () => tenants.find((tenant) => tenant.slug === adminSlug)?.name ?? null,
    [adminSlug, tenants]
  );

  function closeDrawer() {
    setDrawerOpen(false);
  }

  function openAdmin() {
    closeDrawer();
    if (adminSlug) {
      router.push({ pathname: '/admin/[tenant]', params: { tenant: adminSlug } });
    }
  }

  return (
    <>
      <View
        className="border-b border-border px-3 pb-2"
        style={{ backgroundColor: background, paddingTop: insets.top + 8 }}
      >
        <View className="h-11 flex-row items-center gap-2">
          <Pressable
            accessibilityLabel="Open navigation"
            accessibilityRole="button"
            className="size-11 items-center justify-center rounded-full active:opacity-70"
            hitSlop={4}
            onPress={() => setDrawerOpen(true)}
          >
            <MenuIcon color={foreground} size={24} strokeWidth={2.25} />
          </Pressable>

          <View className="h-11 min-w-0 flex-1 flex-row items-center gap-2 rounded-full border border-border bg-card px-3">
            <SearchIcon color={foreground} opacity={0.62} size={18} />
            <TextInput
              accessibilityLabel={searchPlaceholder}
              autoCapitalize="none"
              className="min-w-0 flex-1 font-sans text-sm text-foreground"
              onChangeText={onSearchChange}
              placeholder={searchPlaceholder}
              placeholderTextColor={isDark ? 'rgba(255, 255, 255, 0.58)' : 'rgba(26, 26, 26, 0.52)'}
              returnKeyType="search"
            />
          </View>

          {activeCheckInEvent ? (
            <Pressable
              accessibilityHint="Opens the camera scanner immediately"
              accessibilityLabel={`Check people into ${activeCheckInEvent.title}`}
              accessibilityRole="button"
              className="h-11 flex-row items-center gap-2 rounded-full bg-primary px-3 active:opacity-80"
              hitSlop={4}
              onPress={() => openOperatorCheckIn(activeCheckInEvent.id)}
            >
              <ScanLineIcon color={BRAND.color.white} size={19} strokeWidth={2.4} />
              <Text className="font-bold uppercase text-white">Check in</Text>
            </Pressable>
          ) : null}

          {onCreatePress ? (
            <Pressable
              accessibilityLabel="Create"
              accessibilityRole="button"
              className="size-11 items-center justify-center rounded-full bg-primary active:opacity-80"
              hitSlop={4}
              onPress={onCreatePress}
            >
              <PlusIcon color={BRAND.color.white} size={24} strokeWidth={2.4} />
            </Pressable>
          ) : null}
        </View>
      </View>

      <Modal animationType="fade" onRequestClose={closeDrawer} transparent visible={drawerOpen}>
        <Pressable className="flex-1 bg-black/35" onPress={closeDrawer}>
          <Pressable
            className="h-full w-[84%] max-w-sm overflow-hidden rounded-r-2xl bg-background px-5 pb-5"
            style={{ paddingTop: insets.top + 20 }}
          >
            <View className="gap-5">
              <View className="flex-row items-center justify-between gap-4">
                <View>
                  <Text variant="label" className="text-muted-foreground">
                    6am BKK
                  </Text>
                  <Text variant="h3">Navigation</Text>
                </View>
                <GlassIconButton
                  accessibilityLabel="Close navigation"
                  onPress={closeDrawer}
                  systemImage="xmark"
                >
                  <Icon as={XIcon} className="size-5 text-foreground" />
                </GlassIconButton>
              </View>

              <DrawerSections
                adminLabel={adminTenantName}
                canOpenAdmin={canOpenAdmin}
                onClose={closeDrawer}
                onOpenAdmin={openAdmin}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

function MemberHeaderBarNative({
  activeCheckInEvent,
  searchPlaceholder,
  onCreatePress,
  onSearchChange,
}: Readonly<MemberHeaderBarProps & { activeCheckInEvent: LiveCheckInEvent | null }>) {
  const { colorScheme } = useColorScheme();
  const { isAdmin } = useSession();
  const { memberships, resolveDefaultSlug, tenants } = useTenant();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const isDark = colorScheme === 'dark';
  const headerForeground = isDark ? BRAND.color.white : BRAND.color.ink;
  const searchBarTint = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(26, 26, 26, 0.08)';
  const searchHint = isDark ? 'rgba(255, 255, 255, 0.68)' : 'rgba(26, 26, 26, 0.58)';

  const canOpenAdmin =
    isAdmin ||
    memberships.some((membership) => {
      return membership.role === 'admin' || membership.role === 'owner';
    });

  const adminSlug = React.useMemo(() => {
    if (isAdmin) {
      return tenants[0]?.slug ?? resolveDefaultSlug();
    }
    const adminMembership = memberships.find(
      (membership) => membership.role === 'admin' || membership.role === 'owner'
    );
    return tenants.find((tenant) => tenant.id === adminMembership?.tenant_id)?.slug ?? null;
  }, [isAdmin, memberships, resolveDefaultSlug, tenants]);

  const adminTenantName = React.useMemo(
    () => tenants.find((tenant) => tenant.slug === adminSlug)?.name ?? null,
    [adminSlug, tenants]
  );

  function openAdmin() {
    setDrawerOpen(false);
    if (adminSlug) {
      router.push({ pathname: '/admin/[tenant]', params: { tenant: adminSlug } });
    }
  }

  return (
    <>
      <Stack.Screen
        options={{
          headerBackVisible: false,
          headerShadowVisible: false,
          headerTitle: '',
          headerTransparent: true,
        }}
      />
      <Stack.SearchBar
        allowToolbarIntegration
        autoCapitalize="none"
        barTintColor={searchBarTint}
        headerIconColor={headerForeground}
        hideWhenScrolling={false}
        hintTextColor={searchHint}
        obscureBackground={false}
        onCancelButtonPress={() => onSearchChange?.('')}
        onChangeText={(event) => onSearchChange?.(event.nativeEvent.text)}
        onSearchButtonPress={(event) => onSearchChange?.(event.nativeEvent.text)}
        placement="integratedButton"
        placeholder={searchPlaceholder}
        shouldShowHintSearchIcon
        textColor={headerForeground}
        tintColor={headerForeground}
      />
      <Stack.Toolbar placement="left">
        <Stack.Toolbar.Button
          accessibilityLabel="Open navigation"
          icon={toolbarIcon('line.3.horizontal')}
          onPress={() => setDrawerOpen(true)}
          tintColor={headerForeground}
        >
          {toolbarFallbackLabel('Menu')}
        </Stack.Toolbar.Button>
      </Stack.Toolbar>
      {activeCheckInEvent || onCreatePress ? (
        <Stack.Toolbar placement="right">
          {activeCheckInEvent ? (
            <Stack.Toolbar.View hidesSharedBackground>
              <Pressable
                accessibilityHint="Opens the camera scanner immediately"
                accessibilityLabel={`Check people into ${activeCheckInEvent.title}`}
                accessibilityRole="button"
                className="h-10 flex-row items-center gap-2 rounded-full bg-primary px-3 active:opacity-80"
                onPress={() => openOperatorCheckIn(activeCheckInEvent.id)}
              >
                <ScanLineIcon color={BRAND.color.white} size={18} strokeWidth={2.4} />
                <Text className="font-bold uppercase text-white">Check in</Text>
              </Pressable>
            </Stack.Toolbar.View>
          ) : null}
          {onCreatePress ? (
            <Stack.Toolbar.Button
              accessibilityLabel="Create"
              icon={toolbarIcon('plus')}
              onPress={onCreatePress}
              separateBackground
              tintColor={headerForeground}
            >
              {toolbarFallbackLabel('Add')}
            </Stack.Toolbar.Button>
          ) : null}
        </Stack.Toolbar>
      ) : null}

      <Modal
        animationType="fade"
        onRequestClose={() => setDrawerOpen(false)}
        transparent
        visible={drawerOpen}
      >
        <Pressable className="flex-1 bg-black/35" onPress={() => setDrawerOpen(false)}>
          <Pressable className="h-full w-[84%] max-w-sm overflow-hidden rounded-r-2xl bg-background">
            <View className="gap-5 p-5 pt-14">
              <View className="flex-row items-center justify-between gap-4">
                <View>
                  <Text variant="label" className="text-muted-foreground">
                    6am BKK
                  </Text>
                  <Text variant="h3">Navigation</Text>
                </View>
                <GlassIconButton
                  accessibilityLabel="Close navigation"
                  onPress={() => setDrawerOpen(false)}
                  systemImage="xmark"
                >
                  <Icon as={XIcon} className="size-5 text-foreground" />
                </GlassIconButton>
              </View>

              <DrawerSections
                adminLabel={adminTenantName}
                canOpenAdmin={canOpenAdmin}
                onClose={() => setDrawerOpen(false)}
                onOpenAdmin={openAdmin}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

export function AccountScopeSwitcher({
  phase,
}: Readonly<{
  phase?: GlassControlPhase;
}>) {
  return <ClubScopeSelect minWidth={MEMBER_HEADER_ACCOUNT_MIN_WIDTH} phase={phase} />;
}

export function ClubScopeSelect({
  minWidth,
  phase,
}: Readonly<{ minWidth?: number; phase?: GlassControlPhase }>) {
  const { isAdmin } = useSession();
  const { activeTenant, memberships, tenants } = useTenant();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const adminTenants = React.useMemo(
    () =>
      tenants.filter((tenant) => {
        if (isAdmin) {
          return true;
        }
        const role = memberships.find((membership) => membership.tenant_id === tenant.id)?.role;
        return role === 'admin' || role === 'owner';
      }),
    [isAdmin, memberships, tenants]
  );
  const selectedValue = activeTenant?.slug ?? PERSONAL_SCOPE_VALUE;
  const selectedLabel =
    adminTenants.find((tenant) => tenant.slug === selectedValue)?.name?.trim() || 'Personal';
  const foregroundClassName = memberHeaderControlForeground(phase);
  const controlWidth = Math.max(minWidth ?? 0, CLUB_SCOPE_COLLAPSED_WIDTH);

  function onValueChange(value: string) {
    setIsExpanded(false);

    if (value === selectedValue) {
      return;
    }

    if (value === PERSONAL_SCOPE_VALUE) {
      router.push('/app/me');
      return;
    }

    router.push({ pathname: '/admin/[tenant]', params: { tenant: value } });
  }

  if (!isExpanded) {
    return (
      <View style={{ width: controlWidth }}>
        <GlassControl
          accessibilityLabel="Open club switcher"
          accessibilityRole="button"
          className="w-full"
          contentClassName="flex-row gap-2 px-4"
          frameHeight={MEMBER_HEADER_CONTROL_SIZE}
          onPress={() => setIsExpanded(true)}
          phase={phase}
          systemImage="person.2"
        >
          <Icon as={UsersIcon} className={`size-5 ${foregroundClassName}`} />
          <Text
            className={`font-sans text-sm font-bold ${foregroundClassName}`}
            numberOfLines={1}
            style={{ width: controlWidth - 84 }}
          >
            {selectedLabel}
          </Text>
        </GlassControl>
      </View>
    );
  }

  return (
    <View style={{ minWidth: controlWidth }}>
      <Host matchContents>
        <Picker selectedValue={selectedValue} onValueChange={onValueChange} appearance="menu">
          <Picker.Item label="Personal" value={PERSONAL_SCOPE_VALUE} />
          {adminTenants.map((tenant) => (
            <Picker.Item key={tenant.id} label={tenant.name} value={tenant.slug} />
          ))}
        </Picker>
      </Host>
    </View>
  );
}

function GlassIconButton({
  children,
  phase,
  systemImage,
  ...props
}: Omit<React.ComponentProps<typeof Pressable>, 'style'> & {
  children: React.ReactNode;
  phase?: GlassControlPhase;
  style?: StyleProp<ViewStyle>;
  systemImage: string;
}) {
  return (
    <GlassControl
      frameHeight={MEMBER_HEADER_CONTROL_SIZE}
      frameWidth={MEMBER_HEADER_CONTROL_SIZE}
      phase={phase}
      systemImage={systemImage}
      {...props}
    >
      {children}
    </GlassControl>
  );
}

export const memberHeaderColors = {
  active: BRAND.color.orange,
};
